import { useState, useEffect } from 'react';
import {
  Loader2,
  CheckCircle2,
  XCircle,
  Eye,
  EyeOff,
  CreditCard,
  RefreshCw,
  Save,
  AlertCircle,
  Plus,
  ExternalLink,
  Banknote
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { getAuthHeaders } from '../../services/adminApi';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Icon-only dashboard links keep their inline icon but get a 44px touch target.
const STRIPE_ICON_LINK_CLASS = 'inline-flex items-center justify-center rounded text-blue-600 hover:text-blue-800 admin-touch:min-h-[44px] admin-touch:min-w-[44px]';

// A secret field with an embedded show/hide toggle. The toggle is a full-height
// target at the field's end (44px wide on touch layouts, OWZ-72), and the
// input reserves that space so typed text never runs underneath it.
const SecretInput = ({ revealed, onToggle, ...inputProps }) => (
  <div className="relative">
    <Input
      type={revealed ? 'text' : 'password'}
      className="pr-10 admin-touch:pr-12"
      {...inputProps}
    />
    <button
      type="button"
      onClick={onToggle}
      aria-label="Show secret"
      aria-pressed={revealed}
      className="absolute inset-y-0 right-0 flex w-9 items-center justify-center rounded-r-md text-gray-400 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring dark:text-gray-400 admin-touch:w-[44px]"
    >
      {revealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
    </button>
  </div>
);

const StripeSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [testResult, setTestResult] = useState(null);

  const [formData, setFormData] = useState({
    test_mode: true,
    test_publishable_key: '',
    test_secret_key: '',
    live_publishable_key: '',
    live_secret_key: '',
    webhook_secret: '',
    default_price_id: '',
  });

  const [showSecrets, setShowSecrets] = useState({
    test_secret: false,
    live_secret: false,
    webhook_secret: false,
  });

  const [hasChanges, setHasChanges] = useState(false);

  // Product/Price creation state
  const [creatingProduct, setCreatingProduct] = useState(false);
  const [creatingPrice, setCreatingPrice] = useState(false);
  const [productId, setProductId] = useState('');
  const [priceAmount, setPriceAmount] = useState('120.00');

  // Existing prices state
  const [existingPrices, setExistingPrices] = useState([]);
  const [loadingPrices, setLoadingPrices] = useState(false);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/stripe-settings`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
        setFormData(prev => ({
          ...prev,
          test_mode: data.test_mode !== false,
          default_price_id: data.default_price_id || '',
        }));
      }
    } catch (err) {
      setError('Failed to load Stripe settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchPrices = async () => {
    setLoadingPrices(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/stripe/prices`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setExistingPrices(data.prices || []);
      }
    } catch (err) {
      console.error('Failed to fetch prices:', err);
    } finally {
      setLoadingPrices(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  // Fetch prices when settings are loaded and configured
  useEffect(() => {
    if (settings?.test_configured || settings?.live_configured) {
      fetchPrices();
    }
  }, [settings?.test_configured, settings?.live_configured]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    // Only send non-empty values
    const updates = {};
    Object.entries(formData).forEach(([key, value]) => {
      if (typeof value === 'boolean') {
        updates[key] = value;
      } else if (value && value.trim()) {
        updates[key] = value.trim();
      }
    });

    try {
      const response = await fetch(`${API_BASE}/api/admin/stripe-settings`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(updates),
      });

      if (response.ok) {
        const data = await response.json();
        setSettings(data);
        setSuccess('Stripe settings saved');
        setHasChanges(false);
        // Clear secret inputs after save
        setFormData(prev => ({
          ...prev,
          test_secret_key: '',
          live_secret_key: '',
          webhook_secret: '',
        }));
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const err = await response.json();
        setError(err.detail || 'Failed to save');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      const response = await fetch(`${API_BASE}/api/admin/stripe-settings/test`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      setTestResult({
        success: response.ok,
        message: response.ok ? data.message : data.detail
      });
    } catch (err) {
      setTestResult({ success: false, message: 'Network error' });
    } finally {
      setTesting(false);
    }
  };

  const createProduct = async () => {
    setCreatingProduct(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/api/admin/stripe/create-product`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          name: 'Owaza Martial Arts Membership',
          description: 'Monthly martial arts training membership'
        }),
      });

      const data = await response.json();
      if (response.ok) {
        setProductId(data.product_id);
        setSuccess(`Product created: ${data.product_id}`);
        setTimeout(() => setSuccess(null), 5000);
      } else {
        setError(data.detail || 'Failed to create product');
      }
    } catch (err) {
      setError('Network error creating product');
    } finally {
      setCreatingProduct(false);
    }
  };

  const createPrice = async () => {
    if (!productId) {
      setError('Create a product first');
      return;
    }

    setCreatingPrice(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/api/admin/stripe/create-price`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          product_id: productId,
          amount: parseFloat(priceAmount),
          interval: 'month'
        }),
      });

      const data = await response.json();
      if (response.ok) {
        setFormData(prev => ({ ...prev, default_price_id: data.price_id }));
        setSettings(prev => ({ ...prev, default_price_id: data.price_id }));
        setSuccess(`Price created and set as default: ${data.price_id}`);
        setTimeout(() => setSuccess(null), 5000);
      } else {
        setError(data.detail || 'Failed to create price');
      }
    } catch (err) {
      setError('Network error creating price');
    } finally {
      setCreatingPrice(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }

  const isConfigured = settings?.test_mode
    ? settings?.test_configured
    : settings?.live_configured;

  return (
    <div className="space-y-6">
      {/* Main Settings Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-violet-100">
              <CreditCard className="h-5 w-5 text-violet-600" />
            </div>
            <div>
              <CardTitle className="text-lg">Stripe Payments</CardTitle>
              <CardDescription>Configure Stripe subscription billing for member payments (supports cards + ACH bank transfers)</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-700">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2 text-green-700">
              <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
              <span className="text-sm">{success}</span>
            </div>
          )}

          {/* Mode Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div>
              <p className="font-medium">Environment</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {formData.test_mode ? 'Using Test Mode (testing)' : 'Using Live Mode (production)'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-sm ${formData.test_mode ? 'font-medium text-amber-600' : 'text-gray-400'}`}>
                Test
              </span>
              <Switch
                aria-label="Live mode"
                checked={!formData.test_mode}
                onCheckedChange={(checked) => handleInputChange('test_mode', !checked)}
              />
              <span className={`text-sm ${!formData.test_mode ? 'font-medium text-red-600' : 'text-gray-400'}`}>
                Live
              </span>
            </div>
          </div>

          {/* Status */}
          <div className="flex items-center gap-2 text-sm">
            {isConfigured ? (
              <span className="flex items-center gap-1 text-green-600">
                <CheckCircle2 className="h-4 w-4" />
                {formData.test_mode ? 'Test' : 'Live'} Mode Configured
              </span>
            ) : (
              <span className="flex items-center gap-1 text-amber-600">
                <XCircle className="h-4 w-4" />
                Not configured - enter credentials below
              </span>
            )}
          </div>

          {/* Payment Methods Info */}
          <div className="flex items-center gap-4 p-3 bg-violet-50 rounded-lg text-sm text-violet-700">
            <div className="flex items-center gap-1">
              <CreditCard className="h-4 w-4" />
              Cards
            </div>
            <div className="flex items-center gap-1">
              <Banknote className="h-4 w-4" />
              ACH Bank Transfer (0.8%, max $5)
            </div>
          </div>

          {/* Test Credentials */}
          {formData.test_mode && (
            <div className="space-y-4 p-4 border rounded-lg bg-amber-50/30 border-amber-200">
              <h4 className="font-medium flex items-center gap-2">
                Test Credentials
                <a
                  href="https://dashboard.stripe.com/test/apikeys"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open Stripe test API keys"
                  className={STRIPE_ICON_LINK_CLASS}
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
              </h4>
              <div className="grid gap-4">
                <div>
                  <Label>Publishable Key</Label>
                  <Input
                    placeholder="pk_test_..."
                    value={formData.test_publishable_key}
                    onChange={(e) => handleInputChange('test_publishable_key', e.target.value)}
                  />
                  {settings?.test_publishable_key_masked && (
                    <p className="text-xs text-gray-400 mt-1">Current: {settings.test_publishable_key_masked}</p>
                  )}
                </div>
                <div>
                  <Label>Secret Key</Label>
                  <SecretInput
                    placeholder="sk_test_..."
                    value={formData.test_secret_key}
                    onChange={(e) => handleInputChange('test_secret_key', e.target.value)}
                    revealed={showSecrets.test_secret}
                    onToggle={() => setShowSecrets(prev => ({ ...prev, test_secret: !prev.test_secret }))}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Live Credentials */}
          {!formData.test_mode && (
            <div className="space-y-4 p-4 border rounded-lg border-red-200 bg-red-50/30">
              <h4 className="font-medium text-red-800 flex items-center gap-2">
                Live Credentials (Production)
                <a
                  href="https://dashboard.stripe.com/apikeys"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open Stripe live API keys"
                  className={STRIPE_ICON_LINK_CLASS}
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
              </h4>
              <div className="grid gap-4">
                <div>
                  <Label>Publishable Key</Label>
                  <Input
                    placeholder="pk_live_..."
                    value={formData.live_publishable_key}
                    onChange={(e) => handleInputChange('live_publishable_key', e.target.value)}
                  />
                  {settings?.live_publishable_key_masked && (
                    <p className="text-xs text-gray-400 mt-1">Current: {settings.live_publishable_key_masked}</p>
                  )}
                </div>
                <div>
                  <Label>Secret Key</Label>
                  <SecretInput
                    placeholder="sk_live_..."
                    value={formData.live_secret_key}
                    onChange={(e) => handleInputChange('live_secret_key', e.target.value)}
                    revealed={showSecrets.live_secret}
                    onToggle={() => setShowSecrets(prev => ({ ...prev, live_secret: !prev.live_secret }))}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Price ID & Webhook */}
          <div className="space-y-4">
            <div>
              <Label>Default Price ID</Label>
              <Input
                placeholder="price_XXXXXXXXXXXXXXXXX"
                value={formData.default_price_id}
                onChange={(e) => handleInputChange('default_price_id', e.target.value)}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                The Stripe Price ID for monthly subscriptions
              </p>
            </div>
            <div>
              <Label>Webhook Signing Secret</Label>
              <SecretInput
                placeholder="whsec_..."
                value={formData.webhook_secret}
                onChange={(e) => handleInputChange('webhook_secret', e.target.value)}
                revealed={showSecrets.webhook_secret}
                onToggle={() => setShowSecrets(prev => ({ ...prev, webhook_secret: !prev.webhook_secret }))}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Webhook URL: <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded break-all">https://api.owazamartialarts.com/api/stripe/webhook</code>
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2 pt-4 border-t">
            <Button
              variant="outline"
              size="sm"
              onClick={testConnection}
              disabled={testing || !isConfigured}
            >
              {testing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
              Test Connection
            </Button>

            {hasChanges && (
              <Button size="sm" onClick={handleSave} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                Save Settings
              </Button>
            )}
          </div>

          {testResult && (
            <div className={`text-sm p-3 rounded-lg ${testResult.success ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
              {testResult.message}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Setup Helper Card */}
      {isConfigured && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Quick Setup
            </CardTitle>
            <CardDescription>
              Create a Stripe product and price if you haven't already
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              {/* Create Product */}
              <div className="p-4 border rounded-lg space-y-3">
                <h4 className="font-medium">Step 1: Create Product</h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Creates "Owaza Martial Arts Membership" product in Stripe
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={createProduct}
                  disabled={creatingProduct}
                >
                  {creatingProduct ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                  ) : (
                    <Plus className="h-4 w-4 mr-1" />
                  )}
                  Create Product
                </Button>
                {productId && (
                  <p className="text-xs text-green-600">
                    Product ID: {productId}
                  </p>
                )}
              </div>

              {/* Create Price */}
              <div className="p-4 border rounded-lg space-y-3">
                <h4 className="font-medium">Step 2: Create Price</h4>
                <div className="flex items-center gap-2">
                  <Label className="text-sm">Monthly Price:</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={priceAmount}
                    onChange={(e) => setPriceAmount(e.target.value)}
                    className="w-24"
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={createPrice}
                  disabled={creatingPrice || !productId}
                >
                  {creatingPrice ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                  ) : (
                    <Plus className="h-4 w-4 mr-1" />
                  )}
                  Create Price
                </Button>
                {!productId && (
                  <p className="text-xs text-amber-600">
                    Create a product first
                  </p>
                )}
              </div>
            </div>

            <div className="text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
              <strong>Note:</strong> You can also create products and prices directly in the{' '}
              <a
                href="https://dashboard.stripe.com/products"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                Stripe Dashboard
              </a>
              . Just copy the Price ID here when done.
            </div>
          </CardContent>
        </Card>
      )}

      {/* Existing Prices Card */}
      {isConfigured && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">Existing Prices</CardTitle>
                <CardDescription>
                  Recurring prices configured in Stripe
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchPrices}
                disabled={loadingPrices}
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${loadingPrices ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loadingPrices ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : existingPrices.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-4">
                No recurring prices found. Create one above or in the Stripe Dashboard.
              </p>
            ) : (
              <div className="space-y-2">
                {existingPrices.map((price) => (
                  <div
                    key={price.id}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      settings?.default_price_id === price.id
                        ? 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800'
                        : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">
                          ${(price.unit_amount / 100).toFixed(2)}/{price.recurring?.interval || 'month'}
                        </span>
                        {settings?.default_price_id === price.id && (
                          <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 rounded text-xs font-medium">
                            Default
                          </span>
                        )}
                        {!price.active && (
                          <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded text-xs">
                            Archived
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 font-mono mt-1">{price.id}</p>
                      {price.product_name && (
                        <p className="text-xs text-gray-400">{price.product_name}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(price.id);
                          setSuccess('Price ID copied!');
                          setTimeout(() => setSuccess(null), 2000);
                        }}
                      >
                        Copy ID
                      </Button>
                      {settings?.default_price_id !== price.id && price.active && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setFormData(prev => ({ ...prev, default_price_id: price.id }));
                            setHasChanges(true);
                          }}
                        >
                          Set as Default
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
              <strong>Tip:</strong> The default price is used for customers with a $120/month rate.
              For customers with different rates (like $100), the system automatically creates a custom price at checkout.
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default StripeSettings;
